import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/daemq03ep053ijub1e9g";

interface Field {
  label: string;
  name: string;
  placeholder: string;
  required?: boolean;
}

const fields: Field[] = [
  { label: "您的称呼", name: "name", placeholder: "怎么称呼您", required: true },
  { label: "联系方式", name: "contact", placeholder: "手机号 / 邮箱（选填）" },
];

export default function LeakForm() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get("phone_alt") ?? "").trim();

    setPending(true);
    setStatus("idle");
    setFormError("");

    // honeypot hit: silently treat as success without submitting
    if (honeypot) {
      setPending(false);
      setStatus("success");
      form.reset();
      return;
    }

    const body = new URLSearchParams();
    for (const [k, v] of Array.from(fd.entries())) {
      if (k === "phone_alt") continue;
      body.append(k, String(v));
    }

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed && (parsed.code === "OK" || parsed.code === "ok") && !String(msg).includes("spam")) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(String(msg || "提交未成功，请稍后再试。"));
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查后重新提交。");
    } finally {
      setPending(false);
    }
  }

  return (
    <section id="tougao" className="scroll-mt-24 w-full bg-foreground-950">
      <div className="mx-auto grid w-full max-w-[1200px] gap-8 px-4 md:px-6 py-12 md:py-16 lg:grid-cols-2 lg:gap-14">
        <div className="text-background-100">
          <div className="text-xs font-medium tracking-widest text-primary-300 mb-2">SEND A TIP</div>
          <h2 className="font-heading text-3xl font-bold text-background-50 md:text-4xl">提供线索，一起还原真相</h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-background-200/80 md:text-base">
            如果你掌握假期安排、调休、景区或消费领域的第一手信息与内幕线索，欢迎与我们分享。我们会在核实后展开调查，并对提供者信息严格保密。
          </p>

          <div className="mt-8 space-y-4">
            {[
              { icon: "ri-mail-line", title: "投稿邮箱", desc: "tips@maydayleaks.example" },
              { icon: "ri-checkbox-circle-line", title: "处理原则", desc: "信息经交叉核实后才会发布" },
              { icon: "ri-lock-2-line", title: "隐私保护", desc: "所有线索均做匿名化处理" },
            ].map((it) => (
              <div key={it.title} className="flex items-center gap-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-background-100/10 text-primary-300">
                  <i className={`${it.icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
                </span>
                <div>
                  <div className="text-sm font-medium text-background-50">{it.title}</div>
                  <div className="text-xs text-background-200/70">{it.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-readdy-form
          className="rounded-lg bg-background-50 p-5 md:p-7"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={f.name} className={f.name === "contact" ? "" : ""}>
                <label className="mb-1.5 block text-sm font-medium text-foreground-800">
                  {f.label}
                  {f.required && <span className="text-primary-600"> *</span>}
                </label>
                <input
                  name={f.name}
                  required={f.required}
                  placeholder={f.placeholder}
                  className="w-full rounded-md border border-background-300/70 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 transition-colors"
                />
              </div>
            ))}
          </div>

          <div className="mt-4">
            <label className="mb-1.5 block text-sm font-medium text-foreground-800">
              线索详情<span className="text-primary-600"> *</span>
            </label>
            <textarea
              name="detail"
              required
              maxLength={500}
              rows={5}
              placeholder="请尽量描述时间、地点、经过以及您掌握的证据，便于我们核实跟进（500字以内）"
              className="w-full resize-none rounded-md border border-background-300/70 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 transition-colors"
            />
          </div>

          {/* honeypot */}
          <div className="hp-field">
            <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
          </div>

          {status === "success" && (
            <p className="mt-3 flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
              <i className="ri-checkbox-circle-line text-base w-4 h-4 flex items-center justify-center"></i>
              感谢您的信任，线索已收到，我们会尽快核实处理。
            </p>
          )}
          {status === "error" && (
            <p className="mt-3 flex items-start gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm text-primary-900">
              <i className="ri-error-warning-line mt-0.5 text-base w-4 h-4 flex items-center justify-center"></i>
              {formError}
            </p>
          )}

          <button
            type="submit"
            disabled={pending}
            className="mt-4 flex w-full items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 disabled:opacity-60 transition-colors"
          >
            {pending ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin text-base w-4 h-4 flex items-center justify-center"></i>
                提交中…
              </>
            ) : (
              <>
                提交线索
                <i className="ri-send-plane-line ml-1.5 text-sm w-4 h-4 flex items-center justify-center"></i>
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}